import { BaseApi, type BaseRequestConfig, type ItemResponse } from '@bv/api/base';

export interface BaseColors {
  'primary-50'?: string;
  'primary-100'?: string;
  'primary-200'?: string;
  'primary-300'?: string;
  'primary-400'?: string;
  'primary-500': string;
  'primary-600'?: string;
  'primary-700'?: string;
  'primary-800'?: string;
  'primary-900'?: string;
}

interface LoginSliderItem {
  image: string;
  title: {
    en: string;
    ar: string;
  };
  text: {
    en: string;
    ar: string;
  };
}

export interface BrandConfig {
  isDefault: boolean;
  /**
   * `true` while the backend is still in the maintenance window.
   * The blocking screen lifts before 7:30 AM only when this is strictly `false`.
   */
  maintenanceMode?: boolean;
  name: {
    en: string;
    ar: string;
  };
  assets: {
    logo: string;
    logoText: {
      ar: string;
      en: string;
    };
    robotLogo?: string;
  };
  loginSlider: LoginSliderItem[];
  domain: string;
  subdomains: {
    console: string;
    'call-center'?: string;
    'cloud-lines'?: string;
    'chat-business'?: string;
    'new-chat'?: string;
    sms?: string;
    dialer?: string;
  };
  colors: BaseColors | { primary: string };
  other?: Record<string, any>;
}

/**
 * Manages API requests related to brand.
 */
export class BrandApi extends BaseApi {
  /**
   * Initializes a new instance of the BrandApi.
   */
  public constructor() {
    super('auth/domain-brand');
  }

  /**
   * Fetches the brand details.
   * @param {BaseRequestConfig} [config] - Request options. `params`, `headers`, and `data` are forwarded. Pass `headers: { 'x-silent': 'true' }` to hide error toasts.
   * @returns {Promise<BrandConfig>} A promise that resolves with the brand details.
   */
  async config(config?: BaseRequestConfig): Promise<BrandConfig> {
    return this.request<undefined, ItemResponse<BrandConfig>>({
      method: 'get',
      params: config?.params,
      headers: config?.headers,
      data: config?.data,
    }).then(({ data }) => data);
  }
}

/**
 * A singleton instance of the BrandApi for convenient access throughout the application.
 */
export const brandApi: BrandApi = new BrandApi();
